import { createRef, Component } from "react";
import {
  arrayOf,
  bool,
  func,
  number,
  oneOfType,
  shape,
  string,
} from "prop-types";
import styled from "styled-components/macro";
import { rem } from "polished";

import { URL_API_LISTINGS_CONTACT } from "../../constants";
import {
  api,
  getApiErrorMessage,
  scrollIfNeeded,
  unformatSourceDivision,
  validateInput,
} from "../../helpers";
import getValidAttribution from "../analytics/helpers/get-valid-attribution";

import Alert from "../../styles/components/Alert";
import Button from "../../styles/components/Button";
import EmailOptIn from "../email-opt-in";
import Form from "../form";
import Group from "../form/components/Group";
import Label from "../form/components/Label";
import Modal from "../modal";
import Recaptcha from "../recaptcha";
import { RecaptchaDisclaimer } from "../recaptcha/components";
import Row from "../form/components/Row";
import Select from "../form/components/Select";
import Switch from "../form/components/Switch";
import Text from "../form/components/Text";
import TextArea from "../form/components/TextArea";

class ModalContactListing extends Component {
  constructor(props) {
    super(props);

    this.getIntroText = this.getIntroText.bind(this);
    this.getRecaptchaToken = this.getRecaptchaToken.bind(this);
    this.handleApiError = this.handleApiError.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleTradeIn = this.handleTradeIn.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.postData = this.postData.bind(this);

    this.form = createRef();
    this.header = createRef();

    this.state = {
      company: "",
      companyError: null,
      email: "",
      emailError: null,
      inProgress: false,
      message: "",
      messageError: null,
      name: "",
      nameError: null,
      phone: "",
      phoneError: null,
      serverError: null,
      success: false,
      time: "",
      timeError: null,
      tradeIn: false,
      tradeInDescription: "",
      tradeInDescriptionError: null,
      zip: "",
      zipError: null,
    };
  }

  /**
   * Get intro text
   * @returns string
   */
  getIntroText() {
    const { listings, showListingTitle } = this.props;
    let intro = "Please contact me about";

    if (showListingTitle) {
      return (
        <p>
          {intro} {listings.length > 1 ? " these listings: " : "this listing: "}
          {listings
            .map(
              ({ serial, title }) => `${title}${!!serial ? ` (${serial})` : ""}`
            )
            .join(", ")}
          .
        </p>
      );
    } else if (listings.length > 1) {
      return (
        <p>
          {intro} these <strong>{listings.length} listings</strong>.
        </p>
      );
    } else {
      return <p>{intro} this listing.</p>;
    }
  }

  /**
   * Get reCaptcha token
   * @public
   */
  getRecaptchaToken() {
    const { handleApiError } = this;
    const { recaptchaLoaded, requestRecaptchaToken } = this.props;

    if (!recaptchaLoaded || !requestRecaptchaToken) {
      this.handleApiError("reCAPTCHA did not load");
      return false;
    }

    requestRecaptchaToken("contact")
      .then(token => {
        if (!token) {
          handleApiError("reCAPTCHA failed to execute");
          return false;
        }

        this.postData(token);
      })
      .catch(error => handleApiError(error));
  }

  /**
   * Handle API error
   * @param {*}
   * @public
   */
  handleApiError(error) {
    const message = getApiErrorMessage(error);

    this.setState({ inProgress: false, serverError: message }, () =>
      scrollIfNeeded(this.header.current)
    );
  }

  /**
   * Handle input change
   * @param {event|object} e
   * @public
   */
  handleInput(e) {
    const { target: input } = e;
    const field = input.id;
    const value = !!input ? input.value : null;
    const error = validateInput(input);

    if (!!field && field !== "tradeIn") {
      this.setState({ [field]: value, [`${field}Error`]: error });
    }
  }

  /**
   * Handle form submission
   * @param {event} e
   * @public
   */
  handleSubmit(e) {
    e.preventDefault();

    const { current: form } = this.form;

    if (!form) {
      return false;
    }

    if (!form.checkValidity()) {
      [...form.elements].forEach(input => {
        this.handleInput({ target: input });
      });
    } else {
      this.getRecaptchaToken();
    }
  }

  /**
   * Handle trade-in switch
   * @public
   */
  handleTradeIn() {
    this.setState(({ tradeIn }) => ({
      tradeIn: !tradeIn,
      tradeInDescription: "",
    }));
  }

  /**
   * Post data to API
   * @public
   */
  postData(recaptchaToken) {
    this.setState({ inProgress: true });

    const { handleApiError } = this;
    const { analyticsLabel, attribution, emailOptIn, listings } = this.props;
    const {
      company,
      email,
      message,
      name,
      phone,
      time,
      tradeIn,
      tradeInDescription,
      zip,
    } = this.state;
    let requestUrl = URL_API_LISTINGS_CONTACT;
    const { hasAttribution, validAttribution } =
      getValidAttribution(attribution);
    const data = {
      attribution: validAttribution,
      company: company,
      count: `${listings.length} listing${listings.length > 1 ? "s" : ""}`,
      email: email,
      emailOptIn: emailOptIn,
      hasAttribution,
      message: !!message ? message.replace(/(?:\r\n|\r|\n)/g, "<br>") : null,
      name: name,
      phone: !!phone
        ? phone.replace(/.*(\d{3}).*(\d{3}).*(\d{4})(.*)/, "$1-$2-$3$4")
        : null,
      recaptchaToken: recaptchaToken,
      time: time,
      tradeIn: tradeIn,
      tradeInDescription: !!tradeInDescription
        ? tradeInDescription.replace(/(?:\r\n|\r|\n)/g, "<br>")
        : null,
      zip: zip,
    };

    data.sourceDivision = listings.reduce((sources, listing) => {
      if (!!listing.sourceDivision) {
        const sd = unformatSourceDivision(listing.sourceDivision);
        if (!sources.includes(sd)) {
          sources.push(sd);
        }
      }

      return sources;
    }, []);

    // Multiple listings are sent as post data
    // and single listings are handled through the URL
    if (listings.length > 1) {
      data.listings = listings.map(listing => listing.equipmentId);
    } else if (!!listings[0].slug) {
      requestUrl += `/${listings[0].slug}`;
    }

    api
      .post(requestUrl, data)
      .then(response => {
        if (!!response.data && !!response.data.success) {
          if (!!this.props.trackEvent) {
            this.props.trackEvent({
              category: "Form",
              action: "Submit",
              label: `${analyticsLabel} Contact Listing Modal`,
            });
          }

          this.setState(
            { inProgress: false, serverError: null, success: true },
            () => scrollIfNeeded(this.header.current)
          );
        } else {
          handleApiError(response);
        }
      })
      .catch(handleApiError);
  }

  componentDidMount() {
    if (this.props.open && !!this.props.trackModalView) {
      this.props.trackModalView("contactlisting", {
        category: "click",
        action: "Contact Me About This",
        label: this.props.analyticsLabel,
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.open && prevProps.open) {
      this.setState({
        serverError: null,
        success: false,
      });
    }

    if (!prevProps.open && this.props.open && !!this.props.trackModalView) {
      this.props.trackModalView("contactlisting", {
        category: "click",
        action: "Contact Me About This",
        label: this.props.analyticsLabel,
      });
    }
  }

  render() {
    const {
      form,
      getIntroText,
      handleInput,
      handleSubmit,
      handleTradeIn,
      header,
    } = this;
    const { close, listings, open, targetDevice } = this.props;
    const {
      company,
      companyError,
      email,
      emailError,
      inProgress,
      message,
      messageError,
      name,
      nameError,
      phone,
      phoneError,
      serverError,
      success,
      time,
      timeError,
      tradeIn,
      tradeInDescription,
      tradeInDescriptionError,
      zip,
      zipError,
    } = this.state;
    const shouldStack = targetDevice === "mobile";

    return (
      <Modal aria-label="Contact Me" close={close} open={open}>
        <Recaptcha />
        <header ref={header}>
          <h1>
            <strong>Contact Me — {listings.length}</strong>
            {" "}
            {listings.length > 1 ? "Listings" : "Listing"}
          </h1>
          {getIntroText()}
          {serverError && <Alert type="error">{serverError}</Alert>}
          {success && (
            <Alert type="success">Thanks! We’ll be in touch soon.</Alert>
          )}
        </header>
        {!success && (
          <Form onSubmit={handleSubmit} formRef={form}>
            <div className="form-fields">
              <Row stack={shouldStack}>
                <Group error={nameError}>
                  <Label htmlFor="name" required>
                    Name
                  </Label>
                  <Text
                    id="name"
                    maxLength="30"
                    onChange={handleInput}
                    required
                    value={name}
                  />
                </Group>
                <Group error={phoneError}>
                  <Label htmlFor="phone" required>
                    Phone Number
                  </Label>
                  <Text
                    id="phone"
                    maxLength="20"
                    onChange={handleInput}
                    required
                    type="tel"
                    value={phone}
                  />
                </Group>
              </Row>
              <Group error={emailError}>
                <Label htmlFor="email" required>
                  Email
                </Label>
                <Text
                  id="email"
                  maxLength="200"
                  onChange={handleInput}
                  required
                  type="email"
                  value={email}
                />
              </Group>
              <Row stack={shouldStack}>
                <Group error={companyError}>
                  <Label htmlFor="company">Company</Label>
                  <Text
                    id="company"
                    maxLength="200"
                    onChange={handleInput}
                    value={company}
                  />
                </Group>
                <Group error={zipError}>
                  <Label htmlFor="zip" required>
                    ZIP Code
                  </Label>
                  <Text
                    id="zip"
                    maxLength="10"
                    onChange={handleInput}
                    required
                    value={zip}
                  />
                </Group>
              </Row>
              <Group error={messageError}>
                <Label htmlFor="message">Message</Label>
                <TextArea id="message" onChange={handleInput} value={message} />
              </Group>
              <Row>
                <Group
                  className={targetDevice === "desktop" ? "w50" : null}
                  error={timeError}
                  labelOffset={targetDevice === "desktop" ? "medium" : null}
                >
                  <Label htmlFor="time">Best time to reach me</Label>
                  <Select
                    defaultOption="Select Time of Day"
                    id="time"
                    onChange={handleInput}
                    options={[
                      { value: "Morning" },
                      { value: "Afternoon" },
                      { value: "Evening" },
                    ]}
                    value={time}
                  />
                </Group>
              </Row>
              <Row>
                <Group
                  className={targetDevice === "desktop" ? "w50" : null}
                  labelOffset="small"
                >
                  <Label htmlFor="tradeIn">Have a trade-in?</Label>
                  <Switch
                    checked={tradeIn}
                    id="tradeIn"
                    labelChecked="Yes"
                    labelUnchecked="No"
                    onChange={handleTradeIn}
                  />
                </Group>
              </Row>
              {tradeIn && (
                <Group error={tradeInDescriptionError}>
                  <Label htmlFor="tradeInDescription">
                    Tell us about your trade-in
                  </Label>
                  <FieldNote>
                    Include Make, Model, Year, &amp; Serial Number
                  </FieldNote>
                  <TextArea
                    id="tradeInDescription"
                    onChange={handleInput}
                    required
                    value={tradeInDescription}
                  />
                </Group>
              )}
              <EmailOptIn labelOffset="small" />
            </div>
            <RecaptchaDisclaimer className="form-disclaimer" />
            <div className="form-actions">
              <Button
                block={targetDevice === "mobile"}
                color="green400"
                disabled={inProgress}
                size="small"
                square={targetDevice === "mobile"}
                type="submit"
              >
                {inProgress ? "Sending…" : "Send"}
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    );
  }
}

const FieldNote = styled.div`
  font-size: ${rem("14px")};
  font-style: italic;
  margin-bottom: ${rem("14px")};
`;

ModalContactListing.propTypes = {
  analyticsLabel: string,
  close: func.isRequired,
  emailOptIn: bool,
  listings: arrayOf(
    shape({
      equipmentId: oneOfType([number, string]),
    })
  ).isRequired,
  open: bool,
  recaptchaLoaded: bool,
  requestRecaptchaToken: func.isRequired,
  showListingTitle: bool,
  targetDevice: string,
  trackEvent: func,
  trackModalView: func,
  user: shape({
    email: string,
    name: string,
  }),
};

ModalContactListing.defaultProps = {
  analyticsLabel: "modal",
  listings: [],
  open: false,
  showListingTitle: false,
};

ModalContactListing.getDerivedStateFromProps = (nextProps, prevState) => {
  const { user } = nextProps;
  const { email, name, phone } = prevState;
  const nextState = {};

  if (!!user && !!user.email && !email) {
    nextState.email = user.email;
  }

  if (!!user && !!user.name && !name) {
    nextState.name = user.name;
  }

  if (!!user && !!user.phone && !phone) {
    nextState.phone = user.phone;
  }

  return !!Object.keys(nextState).length ? nextState : null;
};

export default ModalContactListing;
